import { PropsWithChildren, useEffect } from "react";

import { authStore, type AuthSessionSnapshot } from "../auth/authStore";
import { getSelfProfile } from "../profile/profileApi";
import {
  applyDocumentTheme,
  normalizeThemePreference,
  readCachedTheme,
  writeCachedTheme,
  type ThemePreference,
} from "./theme";

interface ThemeSyncAuthStore {
  getSnapshot(): AuthSessionSnapshot;
  subscribe(listener: (snapshot: AuthSessionSnapshot) => void): () => void;
}

interface ThemeSyncDependencies {
  authStore: ThemeSyncAuthStore;
  applyTheme: (theme: ThemePreference) => void | Promise<void>;
  readCachedTheme: () => ThemePreference;
  writeCachedTheme: (theme: ThemePreference) => void;
  readProfileTheme: () => Promise<ThemePreference>;
}

function hasAuthenticatedSession(snapshot: AuthSessionSnapshot): boolean {
  return snapshot.accessToken !== null && snapshot.refreshToken !== null;
}

export function startThemeSync(dependencies: ThemeSyncDependencies): () => void {
  let stopped = false;
  let runID = 0;

  const applyCachedTheme = () => {
    const cachedTheme = normalizeThemePreference(dependencies.readCachedTheme());
    void dependencies.applyTheme(cachedTheme);
  };

  const syncWithSnapshot = (snapshot: AuthSessionSnapshot) => {
    runID += 1;
    const currentRunID = runID;

    if (!hasAuthenticatedSession(snapshot)) {
      applyCachedTheme();
      return;
    }

    void dependencies
      .readProfileTheme()
      .then((theme) => {
        if (stopped || currentRunID !== runID) {
          return;
        }

        const normalizedTheme = normalizeThemePreference(theme);
        void dependencies.applyTheme(normalizedTheme);
        dependencies.writeCachedTheme(normalizedTheme);
      })
      .catch(() => {
        if (stopped || currentRunID !== runID) {
          return;
        }

        applyCachedTheme();
      });
  };

  syncWithSnapshot(dependencies.authStore.getSnapshot());
  const unsubscribe = dependencies.authStore.subscribe((snapshot) => {
    syncWithSnapshot(snapshot);
  });

  return () => {
    stopped = true;
    runID += 1;
    unsubscribe();
  };
}

export function ThemeProvider({ children }: PropsWithChildren) {
  useEffect(() => {
    const stop = startThemeSync({
      authStore,
      applyTheme: (theme) => {
        applyDocumentTheme(theme);
      },
      readCachedTheme,
      writeCachedTheme,
      readProfileTheme: async () => {
        const profile = await getSelfProfile();
        return normalizeThemePreference(profile.theme);
      },
    });

    return stop;
  }, []);

  return <>{children}</>;
}
